import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSort,
  faSortUp,
  faSortDown,
  faEllipsisVertical,
  faEdit,
  faTrash,
  faRotateLeft,
  faPlus,
  faEyeSlash,
  faEye
} from '@fortawesome/free-solid-svg-icons';
import { config } from '../../data/config';
import { usersAPI } from './actions';
import Modal from './Modal';
import AddEditUser from './AddEditUser';
import CheckboxDropdown from '../../../../components/CheckboxDropdown/CheckboxDropdown';
import MultiAutocompleteSelect from '../../../../components/Multiautoselecttags/Multiautocompleteselecttags';
import { findKgsPaginated } from '../../../cabinet/wizplain/activity';
import './StaffManagement.css';

// ─── Константи типів користувачів ────────────────────────────────────────────

const USER_GROUPS = {
  admin: {
    label: 'Адміністратори',
    roles: ['admin', 'superadmin'],
    showDistrictFilter: false,
    showKgRoleFilter: false,
    showKgFilter: false,
  },
  moderator: {
    label: 'Модератори',
    roles: ['moderator'],
    showDistrictFilter: true,
    showKgRoleFilter: false,
    showKgFilter: false,
  },
  support: {
    label: 'Техпідтримка',
    // Ролі техпідтримки — адаптуйте під реальні значення з config.roles
    roles: ['techassistance'],
    showDistrictFilter: false,
    showKgRoleFilter: true,
    showKgFilter: false,
  },
  kindergarten: {
    label: 'Садки',
    // Ролі, прив'язані до садка — адаптуйте під реальні значення з config.roles
    roles: ['kindergartenHead', 'educator', 'methodist', 'nurse'],
    showDistrictFilter: true,
    showKgRoleFilter: false,
    showKgFilter: true,
  },
  all: {
    label: 'Всі',
    roles: null, // null = без фільтрації по ролях
    showDistrictFilter: false,
    showKgRoleFilter: false,
    showKgFilter: false,
  },
};

const KG_ROLE_LABELS = {
  kindergartenHead: 'Директор',
  educator: 'Вихователь',
  methodist: 'Вихователь-методист',
  nurse: 'Медична сестра',
};

const SUPPORT_ROLE_LABELS = {
  techassistance: 'Оператор служби техпідтримки',
};

// ─── Головний компонент ───────────────────────────────────────────────────────

const Users = () => {
  const [users, setUsers] = useState([]);
  const [kindergartens, setKindergartens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Активна группа (тип користувачів)
  const [activeGroup, setActiveGroup] = useState('admin');

  // Показувати деактивованих (тогл)
  const [showInactive, setShowInactive] = useState(false);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [activeMenuIndex, setActiveMenuIndex] = useState(null);

  // Фільтри для кожної групи — зберігаємо окремо, щоб не скидати при перемиканні
  const [districtFilter, setDistrictFilter] = useState([]); // масив обраних районів
  const [kgRoleFilter, setKgRoleFilter] = useState([]);     // ролі техпідтримки
  const [kgFilter, setKgFilter] = useState([]);             // конкретні садки (по _id)

  // Пошук садків через API (як в ApplicationsList)
  const [kgOptions, setKgOptions] = useState([]);
  const [kgById, setKgById] = useState({});
  const [kgLoading, setKgLoading] = useState(false);
  const [kgHasMore, setKgHasMore] = useState(false);
  const [kgSearchTerm, setKgSearchTerm] = useState('');
  const [kgPage, setKgPage] = useState(0);

  const loadKindergartens = useCallback(async (search, pageNum = 0, append = false) => {
    if (pageNum === 0) setKgLoading(true);
    try {
      const result = await findKgsPaginated({ search, page: pageNum });
      const newOptions = result.items.map(item => ({
        _id: item._id,
        name: item.name,
        description: item.district || ''
      }));
      setKgOptions(prev => append ? [...prev, ...newOptions] : newOptions);
      setKgById(prev => {
        const next = { ...prev };
        newOptions.forEach(opt => { next[opt._id] = { name: opt.name }; });
        return next;
      });
      setKgHasMore(result.hasMore);
      setKgPage(pageNum);
    } catch (e) {
      console.error('Error loading kindergartens:', e);
    } finally {
      setKgLoading(false);
    }
  }, []);

  const handleKgSearch = useCallback((searchValue) => {
    setKgSearchTerm(searchValue);
    loadKindergartens(searchValue, 0, false);
  }, [loadKindergartens]);

  const handleKgLoadMore = useCallback(() => {
    if (!kgLoading && kgHasMore) {
      loadKindergartens(kgSearchTerm, kgPage + 1, true);
    }
  }, [kgLoading, kgHasMore, kgSearchTerm, kgPage, loadKindergartens]);

  // Модальне вікно
  const [modal, setModal] = useState({
    isOpen: false,
    action: 'add',
    userData: null,
    errorFields: []
  });

  const validUser = (roles, person) => true;

  // Колонки таблиці
  const columns = [
    { key: 'index',    label: '№',              sortable: false, width: '4%'  },
    { key: 'area',     label: 'Заклад/район',   sortable: true,  width: '25%' },
    { key: 'email',    label: 'Email',           sortable: true,  width: '22%' },
    { key: 'userInfo', label: 'Користувач',      sortable: true,  width: '25%' },
    { key: 'authType', label: 'Тип авторизації', sortable: true,  width: '14%' },
    { key: 'actions',  label: 'Дії',             sortable: false, width: '10%' },
  ];

  useEffect(() => {
    if (!validUser(['admin', 'superadmin'])) {
      setError('Доступ заборонено');
      window.location.href = '#/';
      return;
    }
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersRes, kgRes] = await Promise.all([
        usersAPI.getUsers(),
        usersAPI.getKindergartens()
      ]);

      if (usersRes.error || kgRes.error) {
        setError(usersRes.error?.message || kgRes.error?.message);
        return;
      }

      let usersList = usersRes.data.filter(item => !!item.area);
      const kgList = kgRes.data;

      usersList = usersList.map(item => {
        if (!item.area.match(/^[a-z0-9]{24}$/i)) return item;
        const kindergarten = kgList.find(kg => kg._id === item.area);
        return kindergarten ? { ...kindergarten, ...item } : item;
      });

      usersList.sort((a, b) => a.area.localeCompare(b.area));
      setUsers(usersList);
      setKindergartens(kgList);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ─── Фільтрація ──────────────────────────────────────────────────────────────

  const groupConfig = USER_GROUPS[activeGroup];

  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      // Фільтр по активності
      if (!showInactive && !user.isActive) return false;
      if (showInactive && user.isActive) return false;   // показуємо ТІЛЬКИ деактивованих

      // Фільтр по групі ролей
      if (groupConfig.roles !== null && !groupConfig.roles.includes(user.role)) return false;

      // Фільтр по районах
      if (groupConfig.showDistrictFilter && districtFilter.length > 0) {
        if (groupConfig.showKgFilter) {
          // Для садків район зберігається у полі district (підтягується з об'єкта садка)
          if (!districtFilter.some(d => user.district?.startsWith(d.slice(0, 2)))) return false;
        } else {
          // Для модераторів район — це user.area
          if (!districtFilter.includes(user.area)) return false;
        }
      }

      // Фільтр по ролі (техпідтримка або ролі в садку)
      if (kgRoleFilter.length > 0) {
        if (groupConfig.showKgRoleFilter || groupConfig.showKgFilter) {
          if (!kgRoleFilter.includes(user.role)) return false;
        }
      }

      // Фільтр по садку
      if (groupConfig.showKgFilter && kgFilter.length > 0) {
        if (!kgFilter.includes(user.area)) return false;
      }

      return true;
    });
  }, [users, activeGroup, showInactive, districtFilter, kgRoleFilter, kgFilter, groupConfig]);

  // Лічильники для табів — завжди по активних
  const groupCounts = useMemo(() => {
    const counts = {};
    Object.entries(USER_GROUPS).forEach(([key, group]) => {
      counts[key] = users.filter(u => {
        if (!u.isActive) return false;
        if (group.roles === null) return true;
        return group.roles.includes(u.role);
      }).length;
    });
    return counts;
  }, [users]);

  const inactiveCount = useMemo(() => {
    return users.filter(u => {
      if (u.isActive) return false;
      if (groupConfig.roles === null) return true;
      return groupConfig.roles.includes(u.role);
    }).length;
  }, [users, groupConfig]);

  // ─── Сортування ──────────────────────────────────────────────────────────────

  const sortedUsers = useMemo(() => {
    if (!sortConfig.key) return filteredUsers;
    return [...filteredUsers].sort((a, b) => {
      let aV, bV;
      switch (sortConfig.key) {
        case 'area':     aV = a.area || ''; bV = b.area || ''; break;
        case 'email':    aV = a.email || ''; bV = b.email || ''; break;
        case 'userInfo': aV = `${a.lastName || ''} ${a.firstName || ''}`.toLowerCase();
                         bV = `${b.lastName || ''} ${b.firstName || ''}`.toLowerCase(); break;
        case 'authType': aV = config.authType[a.privelegesAuthType] || '';
                         bV = config.authType[b.privelegesAuthType] || ''; break;
        default: return 0;
      }
      if (aV < bV) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aV > bV) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredUsers, sortConfig]);

  const handleSort = (key) => {
    if (!columns.find(c => c.key === key)?.sortable) return;
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (key !== sortConfig.key) return faSort;
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  // ─── Дії ─────────────────────────────────────────────────────────────────────

  const handleMenuToggle = (index) => setActiveMenuIndex(prev => prev === index ? null : index);
  const handleMenuClose = () => setActiveMenuIndex(null);

  const handleActionSelect = (actionId, user) => {
    if (actionId === 'edit') {
      setModal({ isOpen: true, action: 'edit', userData: user, errorFields: [] });
    } else if (actionId === 'delete' || actionId === 'restore') {
      handleUserStatusChange(user, actionId === 'restore');
    }
    setActiveMenuIndex(null);
  };

  const handleUserStatusChange = async (user, activate = false) => {
    try {
      const result = await usersAPI.updateUser(user._id, { ...user, isActive: activate });
      if (result.error) { alert('Помилка: ' + result.error.message); return; }
      alert(`Користувач успішно ${activate ? 'активований' : 'деактивований'}`);
      loadData();
    } catch (err) {
      alert('Помилка: ' + err.message);
    }
  };

  const handleSaveUser = async (userData) => {
    const requiredFields = ['firstName', 'email', 'role', 'area'];
    const errors = requiredFields.filter(field => !userData[field]);
    if (errors.length > 0) {
      setModal(prev => ({ ...prev, errorFields: errors }));
      return;
    }
    try {
      const cleanData = { ...userData, grant: userData.grant || 'R' };
      delete cleanData.id;
      const result = modal.action === 'add'
        ? await usersAPI.addUser(cleanData)
        : await usersAPI.updateUser(userData._id, cleanData);
      if (result.error) { alert('Помилка: ' + result.error.message); return; }
      alert(modal.action === 'add' ? 'Користувач успішно доданий' : 'Користувач успішно змінений');
      closeModal();
      loadData();
    } catch (err) {
      alert('Помилка: ' + err.message);
    }
  };

  const openAddModal = () => setModal({ isOpen: true, action: 'add', userData: null, errorFields: [] });
  const closeModal = () => setModal({ isOpen: false, action: 'add', userData: null, errorFields: [] });

  const copyToExcel = () => {
    const activeUsers = users.filter(u => u.isActive);
    const headers = ['ПІБ', 'email', 'Роль'].join('\t');
    const csv = [headers, ...activeUsers.map(user =>
      [`${user.lastName || ''} ${user.firstName || ''}`, user.email,
       config.rolesName[config.roles.indexOf(user.role)]]
        .map(el => el?.replace(/[\n\t]+/g, ' ') || '').join('\t')
    )].join('\n');
    navigator.clipboard.writeText(csv).catch(() => {
      const ta = document.createElement('textarea');
      ta.value = csv; document.body.appendChild(ta); ta.select();
      document.execCommand('copy'); document.body.removeChild(ta);
    });
  };

  // ─── Допоміжні рендер-компоненти ─────────────────────────────────────────────

  const GetIndexRole = ({ idRole, firstName, lastName }) => {
    const label = KG_ROLE_LABELS[idRole]
      || SUPPORT_ROLE_LABELS[idRole]
      || config.rolesName[config.roles.indexOf(idRole)]
      || idRole;
    return <span>{label}: {firstName} {lastName}</span>;
  };

  const GetInstitutionInfo = ({ user }) => {
    const kgRoles = USER_GROUPS.kindergarten.roles;
    if (kgRoles.includes(user.role)) {
      return (
        <a href={`/#/objpage/${user.area}`} target="_blank" rel="noopener noreferrer">
          {user.name ? (user.name.length > 55 ? user.name.slice(0, 55) + '…' : user.name) : ''}
          {user.number ? ` №${user.number}` : ' б/н'}
        </a>
      );
    }
    return <span>{user.area}</span>;
  };

  const ActionsMenu = ({ onActionSelect, onClose, user, isActiveTab }) => {
    const actions = isActiveTab
      ? [{ id: 'edit', label: 'Редагувати', icon: faEdit }, { id: 'delete', label: 'Деактивувати', icon: faTrash }]
      : [{ id: 'restore', label: 'Активувати', icon: faRotateLeft }, { id: 'edit', label: 'Редагувати', icon: faEdit }];

    return (
      <div className="actions-dropdown">
        {actions.map(action => (
          <button
            key={action.id}
            className="dropdown-item d-flex align-items-center gap-2 p-2"
            onClick={() => onActionSelect(action.id, user)}
            style={{ border: 'none', backgroundColor: 'transparent', width: '100%', textAlign: 'left', fontSize: '14px', cursor: 'pointer' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8f9fa'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <FontAwesomeIcon icon={action.icon} style={{ width: '16px', color: '#6c757d' }} />
            <span style={{ color: action.id === 'delete' ? '#dc3545' : '#212529' }}>{action.label}</span>
          </button>
        ))}
        <div style={{ borderTop: '1px solid #dee2e6', padding: '8px' }}>
          <button className="btn btn-sm btn-outline-secondary w-100" onClick={onClose} style={{ fontSize: '12px' }}>
            Скасувати
          </button>
        </div>
      </div>
    );
  };

  // ─── Панель фільтрів — inline JSX (не компонент, щоб не перемонтувався) ──────

  const districtOptions = config.districts.map(d => ({ value: d, label: d }));

  const handleDistrictToggle = (val) => {
    setDistrictFilter(prev =>
      prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]
    );
  };

  const filtersJSX = groupConfig.showKgRoleFilter ? (
    <CheckboxDropdown
      placeholder="Всі ролі"
      options={Object.entries(SUPPORT_ROLE_LABELS).map(([v, l]) => ({ value: v, label: l }))}
      selectedValues={kgRoleFilter}
      onToggle={val => setKgRoleFilter(prev =>
        prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]
      )}
      onSelectAll={() => setKgRoleFilter(Object.keys(SUPPORT_ROLE_LABELS))}
      onClearAll={() => setKgRoleFilter([])}
      showTags={false}
      className="me-2"
    />
  ) : (
    <>
      {groupConfig.showDistrictFilter && (
        <CheckboxDropdown
          placeholder="Всі райони"
          options={districtOptions}
          selectedValues={districtFilter}
          onToggle={handleDistrictToggle}
          onSelectAll={() => setDistrictFilter(districtOptions.map(o => o.value))}
          onClearAll={() => setDistrictFilter([])}
          showTags={false}
          className="me-2"
        />
      )}
      {groupConfig.showKgFilter && (
        <>
          <div className="me-2" style={{ minWidth: '260px', maxWidth: '260px' }}>
            <MultiAutocompleteSelect
              value={kgFilter}
              onChange={setKgFilter}
              options={kgOptions}
              placeholder="Пошук садочка..."
              fieldsValue={['name']}
              loading={kgLoading}
              hasMore={kgHasMore}
              onSearch={handleKgSearch}
              onLoadMore={handleKgLoadMore}
              extraOptionsById={kgById}
            />
          </div>
          <CheckboxDropdown
            placeholder="Всі ролі"
            options={Object.entries(KG_ROLE_LABELS).map(([v, l]) => ({ value: v, label: l }))}
            selectedValues={kgRoleFilter}
            onToggle={val => setKgRoleFilter(prev =>
              prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]
            )}
            onSelectAll={() => setKgRoleFilter(Object.keys(KG_ROLE_LABELS))}
            onClearAll={() => setKgRoleFilter([])}
            showTags={false}
            className="me-2"
          />
        </>
      )}
    </>
  );

  // ─── Стан завантаження / помилки ─────────────────────────────────────────────

  if (error) return <div className="staff-management"><div className="error-message">{error}</div></div>;
  if (loading) return <div className="staff-management loading">Завантаження...</div>;

  // ─── Рендер ───────────────────────────────────────────────────────────────────

  return (
    <div className="staff-management">
      <main className="staff-main-content queuetable-page">
        <div className="container-fluid">
          <div className="queuetable-content-wrapper">

            {/* ── Таби типів користувачів ── */}
            <div className="staff-year-bar d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <ul className="staff-year-tabs nav nav-tabs">
                  {Object.entries(USER_GROUPS).map(([key, group]) => (
                    <li key={key} className="nav-item">
                      <button
                        className={`nav-link ${activeGroup === key ? 'active' : ''}`}
                        onClick={() => {
                          setActiveGroup(key);
                          setShowInactive(false); // скидаємо тогл при переключенні
                        }}
                      >
                        {group.label}
                        {key !== 'all' && (
                          <span className={`ms-1 badge ${activeGroup === key ? 'bg-primary' : 'bg-secondary'}`}
                            style={{ fontSize: '10px', verticalAlign: 'middle' }}>
                            {groupCounts[key]}
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {/* ── Фільтри + тогл деактивованих + дії ── */}
              <div className="queuetable-switch-wrap d-flex align-items-center flex-wrap gap-2">
                {filtersJSX}

                {/* Тогл деактивованих */}
                <button
                  className={`btn btn-sm ${showInactive ? 'btn-warning' : 'btn-outline-secondary'}`}
                  onClick={() => setShowInactive(prev => !prev)}
                  title={showInactive ? 'Показати активних' : 'Показати деактивованих'}
                >
                  <FontAwesomeIcon icon={showInactive ? faEye : faEyeSlash} className="me-1" />
                  {showInactive
                    ? 'Активні'
                    : <>Деактивовані {inactiveCount > 0 && <span className="badge bg-warning text-dark ms-1">{inactiveCount}</span>}</>
                  }
                </button>

                {validUser(['superadmin']) && (
                  <button onClick={copyToExcel} className="btn btn-sm btn-outline-primary">
                    Скопіювати для Excel
                  </button>
                )}
                <button onClick={openAddModal} className="btn btn-sm btn-primary">
                  <FontAwesomeIcon icon={faPlus} className="me-1" />
                  Додати
                </button>
              </div>
            </div>

            {/* ── Банер деактивованих ── */}
            {showInactive && (
              <div className="alert alert-warning d-flex align-items-center gap-2 mx-3 mt-2 mb-0 py-2"
                style={{ borderRadius: '6px', fontSize: '14px' }}>
                <FontAwesomeIcon icon={faEyeSlash} />
                <span>Показано деактивованих користувачів. Натисніть <strong>Активні</strong> щоб повернутися.</span>
              </div>
            )}

            {/* ── Таблиця ── */}
            <div className="card staff-table-card" style={{ margin: '12px 20px 20px' }}>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table staff-table align-middle mb-0">
                    <thead>
                      <tr>
                        {columns.map(col => (
                          <th
                            key={col.key}
                            style={{ width: col.width, cursor: col.sortable ? 'pointer' : 'default' }}
                            onClick={() => handleSort(col.key)}
                          >
                            <div className="d-flex align-items-center">
                              {col.label}
                              {col.sortable && (
                                <FontAwesomeIcon icon={getSortIcon(col.key)} className="ms-1" style={{ fontSize: '12px' }} />
                              )}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedUsers.map((user, index) => (
                        <tr key={user._id} style={!user.isActive ? { opacity: 0.6 } : {}}>
                          <td>{index + 1}</td>
                          <td><GetInstitutionInfo user={user} /></td>
                          <td>
                            <span className={!user.isActive ? 'text-muted' : ''}>
                              {user.email}
                              {!user.isActive && (
                                <span className="badge bg-secondary ms-1" style={{ fontSize: '10px' }}>неактивний</span>
                              )}
                            </span>
                          </td>
                          <td>
                            <GetIndexRole idRole={user.role} firstName={user.firstName} lastName={user.lastName} />
                          </td>
                          <td>{config.authType[user.privelegesAuthType] || config.authType.none}</td>
                          <td className="text-end">
                            <div className="actions-menu-container" style={{ position: 'relative', display: 'inline-block' }}>
                              <button
                                className="btn btn-sm btn-link text-decoration-none p-0"
                                onClick={() => handleMenuToggle(index)}
                                style={{ color: '#0069ff' }}
                              >
                                <FontAwesomeIcon icon={faEllipsisVertical} />
                              </button>
                              {activeMenuIndex === index && (
                                <ActionsMenu
                                  onActionSelect={handleActionSelect}
                                  onClose={handleMenuClose}
                                  user={user}
                                  isActiveTab={!showInactive}
                                />
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {sortedUsers.length === 0 && (
                    <div className="text-center py-5">
                      <p className="text-muted mb-1">
                        {showInactive ? 'Деактивованих користувачів не знайдено' : 'Активних користувачів не знайдено'}
                      </p>
                      {!showInactive && inactiveCount > 0 && (
                        <p className="text-muted" style={{ fontSize: '13px' }}>
                          У цій групі є {inactiveCount} деактивованих —{' '}
                          <button className="btn btn-link p-0" style={{ fontSize: '13px' }} onClick={() => setShowInactive(true)}>
                            показати
                          </button>
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="d-flex align-items-center justify-content-between queuetable-pagination-wrapper">
                  <div className="queuetable-pagination-info">
                    {showInactive ? 'Деактивованих' : 'Активних'}: {sortedUsers.length}
                    {activeGroup !== 'all' && (
                      <span className="ms-2 text-muted" style={{ fontSize: '12px' }}>
                        / всього в групі: {groupCounts[activeGroup]}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.action === 'add' ? 'Додати користувача' : 'Змінити користувача'}
      >
        <AddEditUser
          action={modal.action}
          data={modal.userData}
          onSave={handleSaveUser}
          onClose={closeModal}
          errorFields={modal.errorFields}
        />
      </Modal>
    </div>
  );
};

export default Users;